import { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { LegacyOpsBanner } from "../components/LegacyOpsBanner";
import {
  apiCall,
  ApiHttpError,
  RouteNotFoundError,
  type EscrowBucketsResponse,
} from "../api/client";

const EVM_ADDR_RE = /^0x[0-9a-fA-F]{40}$/;

export function EscrowBuckets() {
  return (
    <div>
      <PageHeader
        title="Escrow buckets"
        subtitle="Read treasuryWithdrawable + (optional) affiliateWithdrawable on the V3 escrow."
        method="GET"
        path="/v2/admin/bff/escrow/buckets"
      />
      <LegacyOpsBanner what="This is the legacy escrow buckets read view." />
      <AuthRequired>
        <EscrowBucketsAuthed />
      </AuthRequired>
    </div>
  );
}

function EscrowBucketsAuthed() {
  const [token, setToken] = useState("");
  const [affiliate, setAffiliate] = useState("");
  const [data, setData] = useState<EscrowBucketsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const tokenValid = EVM_ADDR_RE.test(token);
  const affiliateValid = affiliate === "" || EVM_ADDR_RE.test(affiliate);

  async function load() {
    if (!tokenValid) {
      setError("token must be a 0x-40-hex EVM address");
      return;
    }
    if (!affiliateValid) {
      setError("affiliate, if supplied, must be a 0x-40-hex EVM address");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const q: Record<string, string | undefined> = { token: token.toLowerCase() };
      if (affiliate !== "") q.affiliate = affiliate.toLowerCase();
      setData(
        await apiCall<EscrowBucketsResponse>("/v2/admin/bff/escrow/buckets", {
          method: "GET",
          query: q,
        }),
      );
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <fieldset
        style={{
          border: "1px solid var(--border)",
          borderRadius: 4,
          padding: 12,
          display: "grid",
          gridTemplateColumns: "120px 1fr",
          gap: 8,
        }}
      >
        <legend style={{ padding: "0 6px", color: "var(--fg-dim)" }}>Query</legend>
        <label style={{ alignSelf: "center" }}>token</label>
        <input
          value={token}
          onChange={(e) => setToken(e.target.value.trim())}
          placeholder="0x036cbd53842c5426634e7929541ec2318f3dcf7e (Base Sepolia USDC)"
        />
        <label style={{ alignSelf: "center" }}>affiliate (optional)</label>
        <input
          value={affiliate}
          onChange={(e) => setAffiliate(e.target.value.trim())}
          placeholder="0x... (omit to read treasury bucket only)"
        />
        <div />
        <button onClick={load} disabled={loading || !tokenValid}>
          {loading ? "Loading…" : "Read buckets"}
        </button>
      </fieldset>
      {error && <ErrorBox text={error} />}
      {data && (
        <fieldset
          style={{
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: 12,
            marginTop: 12,
          }}
        >
          <legend style={{ padding: "0 6px", color: "var(--fg-dim)" }}>Buckets</legend>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <Row k="escrow.address" v={data.escrow.address} />
              <Row k="escrow.chainId" v={String(data.escrow.chainId)} />
              <Row k="escrow.network" v={data.escrow.network} />
              <Row k="escrow.abiVersion" v={data.escrow.abiVersion} />
              <Row k="token.address" v={data.token.address} />
              <Row k="token.symbol" v={data.token.symbol} />
              <Row k="token.decimals" v={String(data.token.decimals)} />
              <Row k="buckets.treasuryWithdrawable" v={data.buckets.treasuryWithdrawable} />
              <Row
                k="buckets.affiliateWithdrawable"
                v={data.buckets.affiliateWithdrawable ?? "—"}
              />
              <Row k="buckets.affiliate" v={data.buckets.affiliate ?? "—"} />
            </tbody>
          </table>
        </fieldset>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "6px 8px", color: "var(--fg-dim)", width: 240 }}>{k}</td>
      <td style={{ padding: "6px 8px" }}><code>{v}</code></td>
    </tr>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: 12,
        border: "1px solid var(--danger)",
        color: "var(--danger)",
        borderRadius: 4,
        background: "rgba(255,100,100,0.08)",
      }}
    >
      {text}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof RouteNotFoundError) return `BE route missing: ${e.message}`;
  if (e instanceof ApiHttpError) {
    const reason =
      typeof e.body === "object" && e.body !== null && "reason" in e.body
        ? String((e.body as { reason: unknown }).reason)
        : null;
    return `BE HTTP ${e.status} on ${e.path}${reason ? ` (${reason})` : ""}`;
  }
  return e instanceof Error ? e.message : String(e);
}
